<template>
  <div
    class="post-composer"
    :class="{ 'is-compact': compact, 'has-extra': hasComposerExtra }"
  >
    <div class="composer-head">
      <el-input
        ref="contentInput"
        v-model="content"
        type="textarea"
        :rows="compact ? 3 : 4"
        :maxlength="2000"
        show-word-limit
        resize="none"
        placeholder="分享此刻想法..."
        class="composer-input"
        :class="{ 'is-compact': compact }"
      />
    </div>

    <div v-if="imageList.length" class="composer-images">
      <div v-for="(url, idx) in imageList" :key="url" class="composer-image-item">
        <el-image :src="url" fit="cover" class="composer-image" />
        <button type="button" class="composer-image-remove" @click="removeImage(idx)">
          ×
        </button>
      </div>
    </div>

    <div v-if="selectedTopics.length" class="composer-selected-topics">
      <el-tag
        v-for="topic in selectedTopics"
        :key="topic.id"
        closable
        type="info"
        effect="plain"
        class="composer-topic-tag"
        @close="toggleTopic(topic.id)"
      >
        #{{ topic.name }}
      </el-tag>
    </div>

    <div class="composer-toolbar">
      <div class="composer-toolbar-left">
        <el-popover
          v-model:visible="emojiPopoverVisible"
          placement="bottom-start"
          :width="320"
          trigger="click"
          :show-arrow="true"
          popper-class="composer-emoji-popper"
        >
          <template #reference>
            <el-button
              text
              class="composer-tool-btn composer-emoji-trigger"
              :class="{ 'is-active': emojiPopoverVisible }"
            >
              <el-icon class="composer-tool-icon"><IconEmoji /></el-icon>
              <span>表情</span>
            </el-button>
          </template>
          <div class="emoji-panel">
            <div class="emoji-panel-grid">
              <button
                v-for="(emoji, idx) in emojiList"
                :key="idx"
                type="button"
                class="emoji-panel-item"
                :title="emoji"
                @click="insertEmoji(emoji)"
              >
                {{ emoji }}
              </button>
            </div>
          </div>
        </el-popover>

        <el-upload
          action="#"
          :show-file-list="false"
          :auto-upload="false"
          accept="image/*"
          :disabled="imageList.length >= 9 || uploading"
          :on-change="handleImageChange"
        >
          <el-button
            text
            class="composer-tool-btn"
            :disabled="imageList.length >= 9 || uploading"
          >
            <el-icon class="composer-tool-icon"><Picture /></el-icon>
            <span>图片</span>
          </el-button>
        </el-upload>

        <el-popover
          v-model:visible="topicPopoverVisible"
          placement="bottom-start"
          :width="320"
          trigger="click"
          :show-arrow="true"
          popper-class="composer-topic-popper"
        >
          <template #reference>
            <el-button
              text
              class="composer-tool-btn composer-topic-trigger"
              :class="{ 'is-active': topicPopoverVisible || topicIds.length > 0 }"
            >
              <span class="composer-tool-icon" aria-hidden="true">#</span>
              <span>话题</span>
            </el-button>
          </template>

          <div class="topic-panel">
            <el-input
              v-model="topicKeyword"
              clearable
              placeholder="搜索话题"
              :suffix-icon="Search"
              class="topic-panel-search"
            />
            <div class="topic-panel-list">
              <button
                v-for="topic in filteredTopics"
                :key="topic.id"
                type="button"
                class="topic-panel-item"
                :class="{ 'is-selected': isTopicSelected(topic.id) }"
                @click="toggleTopic(topic.id)"
              >
                <span class="topic-panel-name">#{{ topic.name }}</span>
              </button>
              <div v-if="filteredTopics.length === 0" class="topic-panel-empty">
                暂无话题
              </div>
            </div>
          </div>
        </el-popover>
      </div>

      <el-button
        type="primary"
        :loading="publishing"
        :disabled="!canPublish"
        @click="handlePublish"
      >
        发布
      </el-button>
    </div>
  </div>
</template>

<script>
import { Picture, Search } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { publishPost } from "@/api/post";
import { uploadPostImage } from "@/api/upload";
import { getToken } from "@/utils/authStorage";
import { compressImage, formatSizeMB } from "@/utils/imageCompress";
import IconEmoji from "@/components/icons/IconEmoji.vue";

const MAX_TOPICS = 5;
const MAX_CONTENT = 2000;
/** 帖子配图上传上限（压缩后） */
const MAX_POST_IMAGE_MB = 2;
/** 原图软上限，避免过大文件压爆内存 */
const MAX_SOURCE_IMAGE_MB = 20;

const EMOJI_LIST = [
  "😀",
  "😁",
  "😂",
  "🤣",
  "😃",
  "😄",
  "😅",
  "😆",
  "😉",
  "😊",
  "😋",
  "😎",
  "😍",
  "😘",
  "🥰",
  "😗",
  "😙",
  "😚",
  "🙂",
  "🤗",
  "🤩",
  "🤔",
  "🤨",
  "😐",
  "😑",
  "😶",
  "🙄",
  "😏",
  "😣",
  "😥",
  "😮",
  "🤐",
  "😯",
  "😪",
  "😫",
  "🥱",
  "😴",
  "😌",
  "😛",
  "😜",
  "😝",
  "🤤",
  "😒",
  "😓",
  "😔",
  "😕",
  "🙃",
  "🤑",
  "😲",
  "☹️",
  "🙁",
  "😖",
  "😞",
  "😟",
  "😤",
  "😢",
  "😭",
  "😦",
  "😧",
  "😨",
  "😩",
  "🤯",
  "😬",
  "😰",
  "😱",
  "🥵",
  "🥶",
  "😳",
  "🤪",
  "😵",
  "😡",
  "😠",
  "🤬",
  "😷",
  "🤒",
  "🤕",
  "🤢",
  "🤮",
  "🥴",
  "😇",
  "🥳",
  "🥺",
  "🤠",
  "🤡",
  "🤥",
  "🤫",
  "🤭",
  "🧐",
  "🤓",
  "😈",
  "👍",
  "👎",
  "👏",
  "🙌",
  "🤝",
  "✌️",
  "🤞",
  "🤟",
  "🤘",
  "👌",
  "🙏",
  "💪",
  "👀",
  "❤️",
  "🧡",
  "💛",
  "💚",
  "💙",
  "💜",
  "🖤",
  "💔",
  "❣️",
  "💕",
  "💞",
  "💓",
  "💗",
  "💖",
  "💘",
  "💝",
  "🔥",
  "⭐",
  "🌟",
  "✅",
  "❌",
  "💯",
  "🎉",
  "🎊",
  "🎈",
  "🎁",
  "🌹",
];

export default {
  name: "PostComposer",
  components: { Picture, IconEmoji },
  props: {
    topics: { type: Array, default: () => [] },
    /** 底部弹窗等紧凑布局 */
    compact: { type: Boolean, default: false },
  },
  emits: ["published", "need-login"],
  data() {
    return {
      Search,
      emojiList: EMOJI_LIST,
      content: "",
      topicIds: [],
      topicKeyword: "",
      topicPopoverVisible: false,
      emojiPopoverVisible: false,
      imageList: [],
      uploading: false,
      publishing: false,
    };
  },
  computed: {
    canPublish() {
      return (this.content || "").trim().length > 0 && !this.publishing;
    },
    filteredTopics() {
      const kw = (this.topicKeyword || "").trim().toLowerCase();
      const list = Array.isArray(this.topics) ? this.topics : [];
      if (!kw) return list;
      return list.filter((t) =>
        String(t.name || "")
          .toLowerCase()
          .includes(kw)
      );
    },
    selectedTopics() {
      const idSet = new Set(this.topicIds.map(Number));
      return (this.topics || []).filter((t) => idSet.has(Number(t.id)));
    },
    hasComposerExtra() {
      return this.imageList.length > 0 || this.selectedTopics.length > 0;
    },
  },
  watch: {
    topicPopoverVisible(open) {
      if (!open) this.topicKeyword = "";
    },
  },
  methods: {
    getTextareaEl() {
      return this.$refs.contentInput?.$el?.querySelector?.("textarea") || null;
    },
    insertEmoji(emoji) {
      const text = this.content || "";
      if (text.length + emoji.length > MAX_CONTENT) {
        ElMessage.warning(`内容最多 ${MAX_CONTENT} 字`);
        return;
      }
      const textarea = this.getTextareaEl();
      let start = text.length;
      let end = text.length;
      if (textarea) {
        start = textarea.selectionStart ?? text.length;
        end = textarea.selectionEnd ?? text.length;
      }
      this.content = text.slice(0, start) + emoji + text.slice(end);
      const nextPos = start + emoji.length;
      this.$nextTick(() => {
        const el = this.getTextareaEl();
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextPos, nextPos);
      });
    },
    isTopicSelected(id) {
      return this.topicIds.some((x) => Number(x) === Number(id));
    },
    toggleTopic(id) {
      const nid = Number(id);
      const idx = this.topicIds.findIndex((x) => Number(x) === nid);
      if (idx >= 0) {
        this.topicIds.splice(idx, 1);
        return;
      }
      if (this.topicIds.length >= MAX_TOPICS) {
        ElMessage.warning(`最多选择 ${MAX_TOPICS} 个话题`);
        return;
      }
      this.topicIds.push(nid);
    },
    async handleImageChange(file) {
      const raw = file?.raw;
      if (!raw) return;
      if (!getToken()) {
        this.$emit("need-login");
        return;
      }
      if (!raw.type?.startsWith("image/")) {
        ElMessage.warning("只能上传图片");
        return;
      }
      if (raw.size > MAX_SOURCE_IMAGE_MB * 1024 * 1024) {
        ElMessage.warning(`原图过大，请选择不超过 ${MAX_SOURCE_IMAGE_MB}MB 的图片`);
        return;
      }
      if (this.imageList.length >= 9) {
        ElMessage.warning("最多上传 9 张图片");
        return;
      }
      this.uploading = true;
      try {
        const compressed = await compressImage(raw, {
          maxSizeMB: MAX_POST_IMAGE_MB,
          maxWidthOrHeight: 1920,
        });
        if (compressed.size > MAX_POST_IMAGE_MB * 1024 * 1024) {
          ElMessage.warning(
            `压缩后仍为 ${formatSizeMB(
              compressed.size
            )}MB，超过 ${MAX_POST_IMAGE_MB}MB，请换一张图`
          );
          return;
        }
        const res = await uploadPostImage(compressed);
        if (res?.data?.code === 200 && res.data.data) {
          this.imageList.push(res.data.data);
        } else {
          ElMessage.error(res?.data?.msg || "上传失败");
        }
      } catch (e) {
        console.error(e);
        ElMessage.error("上传失败");
      } finally {
        this.uploading = false;
      }
    },
    removeImage(idx) {
      this.imageList.splice(idx, 1);
    },
    async handlePublish() {
      if (!getToken()) {
        this.$emit("need-login");
        return;
      }
      const text = (this.content || "").trim();
      if (!text) {
        ElMessage.warning("请输入内容");
        return;
      }
      this.publishing = true;
      try {
        const res = await publishPost({
          content: text,
          topicIds: this.topicIds,
          images: this.imageList,
          status: "1",
        });
        if (res?.data?.code === 200) {
          ElMessage.success("发布成功");
          this.content = "";
          this.imageList = [];
          this.topicIds = [];
          this.topicPopoverVisible = false;
          this.emojiPopoverVisible = false;
          this.$emit("published");
        } else {
          ElMessage.error(res?.data?.msg || "发布失败");
        }
      } catch (e) {
        console.error(e);
        ElMessage.error("发布失败");
      } finally {
        this.publishing = false;
      }
    },
  },
};
</script>

<style scoped>
.post-composer {
  background: var(--el-bg-color);
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 15px;
  height: var(--home-top-card-height, 192px);
  min-height: var(--home-top-card-height, 192px);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.post-composer.has-extra {
  height: auto;
}

.post-composer.is-compact {
  height: auto;
  min-height: 0;
  margin-bottom: 0;
  padding: 0;
}

.post-composer.is-compact .composer-head {
  flex: none;
}

.post-composer.is-compact .composer-toolbar {
  margin-top: 10px;
}

.post-composer.is-compact .composer-images,
.post-composer.is-compact .composer-selected-topics {
  margin-top: 8px;
}

.composer-head {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.composer-input {
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.composer-input :deep(.el-textarea) {
  flex: 1;
  height: 100%;
}

.composer-input :deep(.el-textarea__inner) {
  box-shadow: none;
  background: var(--el-fill-color-lighter);
  border-radius: 8px;
  padding: 12px 12px 28px;
  height: 100% !important;
  min-height: 0;
}

.composer-input.is-compact {
  flex: none;
}

.composer-input.is-compact :deep(.el-textarea) {
  flex: none;
  height: auto;
}

.composer-input.is-compact :deep(.el-textarea__inner) {
  height: auto !important;
  min-height: 72px;
  padding: 10px 12px 24px;
}

.composer-input :deep(.el-input__count) {
  bottom: 10px;
  right: 14px;
}

.composer-images {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.composer-image-item {
  position: relative;
  width: 88px;
  height: 88px;
}

.composer-image {
  width: 88px;
  height: 88px;
  border-radius: 6px;
}

.composer-image-remove {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  line-height: 18px;
  padding: 0;
}

.composer-selected-topics {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.composer-topic-tag {
  border-radius: 4px;
}

.composer-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.composer-toolbar-left {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.composer-tool-btn {
  display: inline-flex;
  align-items: center;
}

/* Element Plus 会把 slot 包进一层 span，gap 需加在内层 */
.composer-tool-btn :deep(> span) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.composer-tool-btn :deep(.el-icon),
.composer-tool-btn :deep(.composer-tool-icon) {
  margin: 0 !important;
  font-size: 15px;
  line-height: 1;
  width: 1em;
  height: 1em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* 清掉 EP 默认的 icon + span 外边距，避免和 gap 叠成双倍 */
.composer-tool-btn :deep(.el-icon + span),
.composer-tool-btn :deep([class*="el-icon"] + span) {
  margin-left: 0 !important;
}

.composer-topic-trigger.is-active,
.composer-emoji-trigger.is-active {
  color: var(--el-color-primary);
}
</style>

<style>
/* popper 挂到 body，需非 scoped */
.composer-topic-popper,
.composer-emoji-popper {
  padding: 12px !important;
  border-radius: 8px !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1) !important;
}

.composer-emoji-popper .emoji-panel-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  max-height: 260px;
  overflow-y: auto;
}

.composer-emoji-popper .emoji-panel-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.composer-emoji-popper .emoji-panel-item:hover {
  background: var(--el-fill-color-light);
}

.composer-topic-popper .topic-panel-search {
  margin-bottom: 8px;
}

.composer-topic-popper .topic-panel-search .el-input__wrapper {
  border-radius: 8px;
  background: var(--el-fill-color-light);
  box-shadow: none;
}

.composer-topic-popper .topic-panel-list {
  max-height: 280px;
  overflow-y: auto;
}

.composer-topic-popper .topic-panel-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease;
}

.composer-topic-popper .topic-panel-item:hover {
  background: var(--el-fill-color-light);
}

.composer-topic-popper .topic-panel-item.is-selected {
  background: var(--el-color-primary-light-9);
}

.composer-topic-popper .topic-panel-item.is-selected .topic-panel-name {
  color: var(--el-color-primary);
  font-weight: 600;
}

.composer-topic-popper .topic-panel-name {
  font-size: 14px;
  color: var(--el-text-color-primary);
}

.composer-topic-popper .topic-panel-empty {
  padding: 24px 8px;
  text-align: center;
  color: var(--el-text-color-secondary);
  font-size: 13px;
}
</style>
